import type { InsightGroup } from "@v2/features/infra/analytics/events";

import * as React from "react";

import { skipToken } from "@tanstack/react-query";
import { FAKE_INSIGHT } from "@v1/features/analytics/fakeData";
import { isInsightBuildableInReplo } from "@v1/features/analytics/insights/utils";
import useAnalyticsStatus from "@v2/features/analytics/hooks/useAnalyticsStatus";
import { analyticsRouter } from "@v2/trpc/routes/analytics";
import { addDays, startOfDay } from "date-fns";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

import { useAnalyticsQueryContext } from "./contexts/AnalyticsQueryContext";

const useAnalyticsInsights = (activeInsightGroup: InsightGroup) => {
  const { workspaceId, projectId } = useAnalyticsQueryContext();
  const { shouldBlurFullScreen } = useAnalyticsStatus();

  const rangeStartDate = startOfDay(addDays(new Date(), -7));

  const { data: allInsights, isLoading } = analyticsRouter.getInsights.useQuery(
    (workspaceId || projectId) && !shouldBlurFullScreen
      ? {
          workspaceId: workspaceId ?? undefined,
          projectId: projectId ?? undefined,
          rangeStart: rangeStartDate.valueOf(),
        }
      : skipToken,
  );

  const activeGroupInsights = allInsights?.filter((insight) => {
    return exhaustiveSwitch({ type: activeInsightGroup })({
      "build-in-replo": isInsightBuildableInReplo(insight),
      "trending-up": insight.sentiment === "positive",
      "trending-down": insight.sentiment === "negative",
      other: insight.sentiment === "neutral",
    });
  });

  if (shouldBlurFullScreen) {
    const dummyInsights = Array.from({ length: 4 }, () => FAKE_INSIGHT);

    return {
      activeGroupInsights: dummyInsights,
      allInsights: dummyInsights,
      isLoading: false,
    };
  }

  return {
    activeGroupInsights,
    allInsights,
    isLoading,
  };
};

export default useAnalyticsInsights;
